import React, { useState, useCallback, useMemo } from 'react';
import { Alert } from '@billda/design-system';
import { getErrorMessage } from '@billda/shared-ui';
import { LabourItemGroupDetails } from '../api/labourItemGroupsApi';
import { useLabourGroupsPanelStore } from '../stores/labourGroupsPanelStore';
import { useUnifiedGroupMutation } from '../hooks/useUnifiedGroupMutation';
import { useAvailableLabourItems } from '../hooks/useAvailableLabourItems';
import { GroupNameCard } from './panel/molecules/GroupNameCard';
import { LabourItemsAssignmentCard } from './panel/molecules/LabourItemsAssignmentCard';
import { GroupFormActions } from './panel/molecules/GroupFormActions';

interface GroupEditFormProps {
  group: LabourItemGroupDetails;
}

const GroupEditForm: React.FC<GroupEditFormProps> = ({ group }) => {
  const editedGroupName = useLabourGroupsPanelStore(
    state => state.editedGroupName
  );
  const setEditedGroupName = useLabourGroupsPanelStore(
    state => state.setEditedGroupName
  );
  const transferListSearch = useLabourGroupsPanelStore(
    state => state.transferListSearch
  );
  const setTransferListSearch = useLabourGroupsPanelStore(
    state => state.setTransferListSearch
  );
  const switchMode = useLabourGroupsPanelStore(state => state.switchMode);

  const [assignedItems, setAssignedItems] = useState(group.labourItems);

  const { data: allAvailableItems = [], isLoading: itemsLoading } =
    useAvailableLabourItems();

  const availableItems = useMemo(() => {
    const assignedIds = new Set(assignedItems.map(item => item.id));
    return allAvailableItems.filter(item => !assignedIds.has(item.id));
  }, [allAvailableItems, assignedItems]);

  const {
    mutate: saveGroup,
    isPending,
    error: mutationError,
  } = useUnifiedGroupMutation();

  const hasChanges = useMemo(() => {
    if (editedGroupName.trim() !== group.name) {
      return true;
    }
    const originalIds = new Set(group.labourItems.map(item => item.id));
    return (
      originalIds.size !== assignedItems.length ||
      assignedItems.some(item => !originalIds.has(item.id))
    );
  }, [assignedItems, editedGroupName, group]);

  const handleSave = useCallback(() => {
    if (!hasChanges) {
      switchMode('readonly');
      return;
    }

    saveGroup(
      {
        groupId: group.id,
        name: editedGroupName.trim(),
        labourItemDefinitionIds: assignedItems.map(item => item.id),
      },
      {
        onSuccess: () => switchMode('readonly'),
      }
    );
  }, [
    assignedItems,
    editedGroupName,
    group,
    hasChanges,
    saveGroup,
    switchMode,
  ]);

  const handleCancel = () => {
    setEditedGroupName(group.name);
    setAssignedItems(group.labourItems);
    setTransferListSearch('');
    switchMode('readonly');
  };

  const handleMoveToAssigned = useCallback(
    (itemId: string) => {
      const item = availableItems.find(item => item.id === itemId);
      if (item) {
        setAssignedItems(prev => [...prev, item]);
      }
    },
    [availableItems]
  );

  const handleMoveToAvailable = useCallback((itemId: string) => {
    setAssignedItems(prev => prev.filter(item => item.id !== itemId));
  }, []);

  const isFormValid = editedGroupName.trim().length > 0;

  return (
    <div className="flex flex-col gap-6">
      {mutationError && (
        <Alert severity="error">{getErrorMessage(mutationError)}</Alert>
      )}

      <GroupNameCard
        name={editedGroupName}
        onChange={setEditedGroupName}
        disabled={isPending}
        dataTestId="group-name-input"
      />

      <LabourItemsAssignmentCard
        availableItems={availableItems}
        assignedItems={assignedItems}
        searchText={transferListSearch}
        onSearchChange={setTransferListSearch}
        onMoveToAssigned={handleMoveToAssigned}
        onMoveToAvailable={handleMoveToAvailable}
        loading={itemsLoading || isPending}
      />

      <GroupFormActions
        onCancel={handleCancel}
        onSubmit={handleSave}
        submitLabel="Save Changes"
        disabled={!isFormValid || !hasChanges}
        loading={isPending}
        status={hasChanges ? 'Unsaved changes' : undefined}
        cancelTestId="cancel-edit-button"
        submitTestId="save-group-button"
      />
    </div>
  );
};

export default GroupEditForm;
